import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { utils, read } from "xlsx";
import { AxiosGraphQlAction, Helper, Pattern } from "../../services";
import { Table, SectionTitle, Alert } from "../../shared";
import { getLmbcList, setLmbc } from "../reducers";
import Action from "./action";
import { GET_ALL_DERMATOLOGIST as query, CUSTOM_CREATE_ABBVIE_LMBC_HCP } from "./query";

import FileType from "file-type";
import { type IAlert, type DataTypes, type IVariables } from "./types";
import { type AxiosResponse } from "axios";
import ReactExport from "react-excel-exportz";
import { toast } from "react-toastify";
import { ERROR_CODE_LIST as codeList } from "../../../utils";

const columns = [
    {
        accessorKey: "email",
        header: "Email",
    },
    {
        accessorKey: "clinicName",
        header: "Clinic Name",
    },
    {
        accessorKey: "address",
        header: "Address",
    },
    {
        accessorKey: "country",
        header: "Country",
        cell: (cell: any) => Helper.GetCountryName(cell.getValue()),
    },
];

const renderSubComponent = (data: any) => {
    return <Action data={data.row.original} />;
};

const DermFinder = (props: any) => {
    const [loading, setLoading] = useState(true);
    const defaultAlert: IAlert = {
        type: "",
        message: "",
    };
    const [alert, setAlert] = useState(defaultAlert);
    const [state, setState] = useState({
        importType: "",
    });
    const resetAlert = () => {
        setTimeout(() => {
            setAlert(defaultAlert);
        }, 3000);
    };

    const ExcelFile = ReactExport.ExcelFile;
    // @ts-expect-error: unexpected
    const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
    // @ts-expect-error: unexpected
    const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

    const createArrToStr = (value: string) => {
        let arr = [];
        if (value) {
            arr = value.split(",").map((item: any) => item.toString().trim());
        }
        const newArra = arr.filter(Boolean);
        return newArra;
    };

    const validateInputs = (data: any) => {
        let isValid = true;
        const invalidFields: string[] = [];
        const missingFields: string[] = [];
        const invalidIndexes: number[] = [];

        data.forEach((item: any, index: number) => {
            let pushIndex = false;

            !item.firstName && missingFields.push("First Name") && (pushIndex = true);
            !item.lastName && missingFields.push("Last Name") && (pushIndex = true);
            !item.email && missingFields.push("Email") && (pushIndex = true);
            !item.gender && missingFields.push("Gender") && (pushIndex = true);
            !item.country && missingFields.push("Country") && (pushIndex = true);
            !item.address && missingFields.push("Address") && (pushIndex = true);
            !item.clinicName && missingFields.push("Clinic Name") && (pushIndex = true);

            if (item.email && !Pattern.email.test(item.email)) {
                isValid = false;
                invalidFields.push(item.email);
                pushIndex = true;
            } else if (item.phone && !Pattern.number.test(item.phone)) {
                isValid = false;
                invalidFields.push(item.phone);
                pushIndex = true;
            } else if (item.website && !Pattern.link.test(item.website)) {
                isValid = false;
                invalidFields.push(item.website);
                pushIndex = true;
            } else if (item.firstName && !Pattern.text.test(item.firstName)) {
                isValid = false;
                invalidFields.push(item.firstName);
                pushIndex = true;
            } else if (item.lastName && !Pattern.text.test(item.lastName)) {
                isValid = false;
                invalidFields.push(item.lastName);
                pushIndex = true;
            } else if (item.language && !Pattern.text.test(item.language)) {
                isValid = false;
                invalidFields.push(item.language);
                pushIndex = true;
            } else if (item.region && !Pattern.text.test(item.region)) {
                isValid = false;
                invalidFields.push(item.region);
                pushIndex = true;
            }
            pushIndex && invalidIndexes.push(++index);
        });
        return {
            isValid,
            invalidFields,
            missingFields,
            invalidIndexes,
        };
    };

    const redirect = () => {
        setTimeout(() => {
            document.location.pathname = "/abbvie-lmbc/derm-finder";
        }, 2000);
    };

    const insertIntoDatabase = (data: DataTypes[]) => {
        const newData: any[] = [];
        const countryList: string[] = [];
        const duplicateEmails: string[] = [];
        const duplicateAddresses: string[] = [];

        data.forEach((item: any, index: number) => {
            if (item ?? item.email) {
                const dermatologist = {
                    firstName: item.firstName || "",
                    lastName: item.lastName || "",
                    email: item.email || "",
                    phone: item.phone?.toString() || "",
                    gender: item.gender?.toUpperCase() || "",
                    website: item.website || "",
                    country: item.country?.toUpperCase() || "",
                    imageId: item.imageId?.toString() || "",
                    address: item.address || "",
                    region: item.region || "",
                    clinicName: item.clinicName || "",
                    monday: createArrToStr(item.monday) || [],
                    tuesday: createArrToStr(item.tuesday) || [],
                    wednesday: createArrToStr(item.wednesday) || [],
                    thursday: createArrToStr(item.thursday) || [],
                    friday: createArrToStr(item.friday) || [],
                    saturday: createArrToStr(item.saturday) || [],
                    sunday: createArrToStr(item.sunday) || [],
                    language: createArrToStr(item.language) || [],
                };

                newData.push(dermatologist);

                if (item.country && !countryList.includes(item.country.toUpperCase())) {
                    countryList.push(item.country.toUpperCase());
                }

                const hasDuplicateEmail =
                    newData.filter((item: any) => item.email === dermatologist.email).length > 1;
                if (hasDuplicateEmail) {
                    duplicateEmails.push(dermatologist.email);
                }

                const hasDuplicateAddress =
                    newData.filter((item: any) => item.address === dermatologist.address).length >
                    1;
                if (hasDuplicateAddress) {
                    duplicateAddresses.push(dermatologist.address);
                }
            }
        });

        const variables: IVariables = {
            abbvieLmbcHcp: newData,
            insertType: state.importType,
            countryList,
        };

        let { isValid, invalidFields, missingFields, invalidIndexes } = validateInputs(
            variables.abbvieLmbcHcp
        );

        if (
            isValid &&
            missingFields.length === 0 &&
            invalidFields.length === 0 &&
            duplicateEmails.length === 0 &&
            duplicateAddresses.length === 0
        ) {
            AxiosGraphQlAction(variables, CUSTOM_CREATE_ABBVIE_LMBC_HCP)
                .then((result: AxiosResponse) => {
                    setLoading(true);
                    const createText = state.importType === "new" ? "Created" : state.importType;
                    if (result?.data?.data?.insertAbbvieLmbcHcp) {
                        setAlert({
                            type: "success",
                            message: `Dermatologist successfully ${createText}`,
                        });
                    } else {
                        const errCode: string | number = result.data.errors[0].errcode;
                        // @ts-expect-error: Unreachable code error
                        const errMsg: string = codeList[errCode] || "Something went wrong";

                        setAlert({
                            type: "danger",
                            message: errMsg,
                        });
                        resetAlert();
                    }
                    setLoading(false);
                    redirect();
                })
                .catch((err) => {
                    setLoading(false);
                    setAlert({
                        type: "danger",
                        message: "Something went wrong, please try again later",
                    });
                    resetAlert();
                    throw new Error(err);
                });
        } else {
            let message: string = "";
            if (missingFields.length > 0) {
                // @ts-expect-error : Unreachable code error
                missingFields = [...new Set(missingFields)];
                message += `Missing mandatory fields: ${missingFields.join(", ")}.`;
            }
            if (invalidFields.length > 0) {
                // @ts-expect-error: Unreachable code error
                invalidFields = [...new Set(invalidFields)];
                message += ` Invalid fields: ${invalidFields.join(", ")}.`;
            }
            if (invalidIndexes.length > 0) {
                message += ` Invalid indexes: ${invalidIndexes.join(", ")}.`;
            }
            if (duplicateEmails.length > 0) {
                message += ` Duplicate emails exists.`;
            }
            if (duplicateAddresses.length > 0) {
                message += ` Duplicate addresses exists.`;
            }
            setAlert({
                type: "danger",
                message,
            });
            resetAlert();
            setLoading(false);
            redirect();
        }
    };

    const fileTypeCheck = async (file: any) => {
        const fileType = await FileType.fromBuffer(file);
        return fileType;
    };

    const dataImport = (e: any) => {
        setLoading(true);
        const f = e.target.files[0];
        const reader = new FileReader();
        reader.onloadend = (evt: any) => {
            if (evt.target.readyState === FileReader.DONE) {
                const data = new Uint8Array(evt.target.result);
                fileTypeCheck(data)
                    .then((result: any) => {
                        if (result.ext === "xlsx") {
                            const workbook = read(data, { type: "array" });
                            const firstSheetName = workbook.SheetNames[0];
                            const worksheet = workbook.Sheets[firstSheetName];
                            const allData: DataTypes[] = utils.sheet_to_json(worksheet);
                            if (allData.length > 0) {
                                insertIntoDatabase(allData);
                            } else {
                                setAlert({
                                    type: "danger",
                                    message: `something went wrong please try again later`,
                                });
                                resetAlert();
                                setLoading(false);
                            }
                        } else {
                            setAlert({
                                type: "danger",
                                message: `Invalid File. Please upload Valid file.`,
                            });
                            resetAlert();
                            setLoading(false);
                        }
                    })
                    .catch((err: any) => {
                        setLoading(false);
                        toast.error(err.message);
                    });
            }
        };
        reader.readAsArrayBuffer(f);
    };

    const xlHandleChange = ({ currentTarget: input }: any) => {
        const { name, value } = input;

        if (name !== "") {
            setState({ importType: value });
        } else {
            setState({ importType: "" });
        }
    };

    const importTypeList = [
        { id: "new", name: "Add New" },
        { id: "update", name: "Update By Email" },
        { id: "replaceByCountry", name: "Replace All By Country" },
    ];

    const sampleDataColumnList = [
        "firstName",
        "lastName",
        "email",
        "phone",
        "gender",
        "website",
        "country",
        "imageId",
        "address",
        "region",
        "clinicName",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
        "language",
    ];

    useEffect((): (() => void) => {
        let isApiCalled = true;
        if (isApiCalled) {
            const variables = {};

            AxiosGraphQlAction(variables, query)
                .then((response) => {
                    const lmbc = response.data.data.allAbbvieLmbcHcps.nodes;
                    props.setLmbc(lmbc);
                    setLoading(false);
                })
                .catch((err) => {
                    throw new Error(err);
                });
        }
        return () => {
            props.setLmbc([]);
            isApiCalled = false;
        };
    }, []);

    const [filter, setFilter] = useState(
        props.countryList.length !== 0
            ? {
                  country: {
                      in: [...props.countryList],
                  },
              }
            : {
                  address: {
                      notEqualTo: "",
                  },
              }
    );

    const [{ pageIndex, pageSize }, setPagination] = React.useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = React.useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize]
    );

    const [tableInfo, setTableInfo] = useState({
        pageIndex,
        pageSize,
        totalCount: 0,
        pageInfo: {
            hasNextPage: undefined,
            hasPreviousPage: undefined,
            startCursor: undefined,
            endCursor: undefined,
        },
    });

    useEffect(() => {
        setLoading(true);
        let isApiCalled = true;
        if (isApiCalled) {
            const { startCursor, endCursor } = tableInfo.pageInfo;
            let variables = {};

            if (pageIndex > tableInfo.pageIndex) {
                variables = {
                    filter: {
                        ...filter,
                    },
                    last: undefined,
                    before: undefined,
                    first: endCursor ? pageSize : undefined,
                    after: endCursor,
                };
            } else if (pageIndex < tableInfo.pageIndex) {
                variables = {
                    filter: {
                        ...filter,
                    },
                    last: startCursor ? pageSize : undefined,
                    before: startCursor,
                    first: startCursor ? undefined : pageSize,
                    after: undefined,
                };
            } else {
                variables = {
                    filter: {
                        ...filter,
                    },
                    first: pageSize,
                    after: undefined,
                    last: undefined,
                    before: undefined,
                };
            }

            AxiosGraphQlAction(variables, query)
                .then(({ data }) => {
                    const lmbc = data.data.allAbbvieLmbcHcps.nodes;
                    const pageInfo = data.data.allAbbvieLmbcHcps.pageInfo;
                    const totalCount = data.data.allAbbvieLmbcHcps.totalCount;

                    setTableInfo({
                        pageIndex,
                        pageSize,
                        totalCount,
                        pageInfo: { ...pageInfo },
                    });

                    props.setLmbc(lmbc);
                    setLoading(false);
                })
                .catch((err) => {
                    setLoading(false);
                    throw new Error(err);
                });
        }
        return () => {
            props.setLmbc([]);
            isApiCalled = false;
        };
    }, [pageIndex, pageSize, filter]);

    const handleChange = (value: string, columnName: string) => {
        const filterType = columnName === "country" ? "in" : "includesInsensitive";
        const defaultValue = columnName === "country" ? [...props.countryList] : "";

        if (value) {
            setFilter({
                ...filter,
                [columnName]: {
                    [filterType]: value,
                },
            });
        } else {
            if (columnName === "country" && props.countryList.length === 0) {
                delete filter?.country;
                setFilter({
                    ...filter,
                });
            } else {
                setFilter({
                    ...filter,
                    [columnName]: {
                        [filterType]: defaultValue,
                    },
                });
            }
        }
    };

    const pageCount = Math.ceil(tableInfo.totalCount / pageSize);

    const pageSizeChangeAction = () => {
        setTableInfo({
            ...tableInfo,
            pageInfo: {
                hasNextPage: undefined,
                hasPreviousPage: undefined,
                startCursor: undefined,
                endCursor: undefined,
            },
        });
    };

    return (
        <>
            <SectionTitle
                backUrl="/"
                title="Dermatologist List"
                buttonText="Create Dermatologist"
                buttonUrl="/abbvie-lmbc/derm-finder/create"
            />

            {alert.type && alert.message ? (
                <Alert alertType={alert.type} alertMessage={alert.message} />
            ) : (
                ""
            )}

            <div className="row justify-content-end">
                <div className="col-md-7 text-right d-flex justify-content-end">
                    <form id="importDataForm">
                        <input
                            style={{ display: "none" }}
                            onChange={dataImport}
                            type="file"
                            className="custom-file-input"
                            id="inputGroupFile"
                        />
                    </form>
                    <select
                        value={state.importType}
                        onChange={xlHandleChange}
                        name="type"
                        id="type"
                        className="form-group form-control col-6 mx-2 "
                        style={{ height: "47px", display: "initial" }}
                    >
                        <option value=""> Select Import Type</option>
                        {importTypeList.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                    {loading ? (
                        <label>
                            <button
                                className="btn btn-primary"
                                type="button"
                                disabled
                                style={{ height: "50px" }}
                            >
                                <span
                                    className="spinner-border spinner-border-sm mr-1"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Loading...
                            </button>
                        </label>
                    ) : (
                        state.importType &&
                        !loading && (
                            <button
                                className="btn btn-primary mb-2"
                                type="button"
                                style={{ height: "50px" }}
                            >
                                <label
                                    style={{
                                        cursor: "pointer",
                                        color: "white",
                                    }}
                                    htmlFor="inputGroupFile"
                                >
                                    <span className="mr-1">
                                        <i className="fa fa-file-excel-o"></i>
                                    </span>
                                    Import Excel
                                </label>
                            </button>
                        )
                    )}
                    {!state.importType && !loading && (
                        <ExcelFile
                            filename="DermFingerSampleData"
                            element={
                                <button
                                    className="btn btn-outline-primary mb-2 py-2 px-4"
                                    style={{ height: "50px" }}
                                >
                                    Sample Excel
                                </button>
                            }
                        >
                            <ExcelSheet data={[]} name="DermFinderSampleDataSheet">
                                {sampleDataColumnList.map((item, index) => (
                                    <ExcelColumn key={item} label={item} />
                                ))}
                            </ExcelSheet>
                        </ExcelFile>
                    )}
                </div>
            </div>

            <Table
                data={props.lmbc}
                headers={columns}
                getRowCanExpand={() => true}
                renderSubComponent={renderSubComponent}
                loading={loading}
                pageCount={pageCount}
                totalCount={tableInfo.totalCount}
                pagination={pagination}
                onPaginationChange={setPagination}
                handleFilter={(value: string, columnName: string) =>
                    handleChange(value, columnName)
                }
                pageSizeChangeAction={() => pageSizeChangeAction()}
            />
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        lmbc: getLmbcList(state),
        countryList: state.users.currentUser.userType === 1 ? state.users.currentUser.country : [],
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        setLmbc: (list: any) => {
            dispatch(setLmbc(list));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DermFinder);
